import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, Clock, BookOpen, Users, Phone, MapPin, Trophy, Target, FileText, ChevronRight, Star, Shield, Send, Award, Briefcase, Newspaper, Bell, HelpCircle, Video, Instagram, CheckCircle2, ExternalLink, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://customer-assets.emergentagent.com/job_signature-psc-portal/artifacts/h1jchf2q_IMG-20260126-WA0188.jpg";
const WA_NUMBER = "918281583404";
const PHONE = "8281583404";
const INSTAGRAM_URL = "https://www.instagram.com/signaturepsc";
const GOOGLE_MAPS_URL = "https://share.google/7yt0Iq3EGuRAwfZuV";
const GOOGLE_REVIEWS_COUNT = "172+";

export { API, LOGO, WA_NUMBER, PHONE, INSTAGRAM_URL, GOOGLE_MAPS_URL, GOOGLE_REVIEWS_COUNT };

function PhotoImg({ path, alt, className }) {
  if (!path) return null;
  const src = path.startsWith('http') ? path : `${API}/files/${path}`;
  return <img src={src} alt={alt} className={className} />;
}

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: "easeOut" } })
};

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0c1a0e]/95 backdrop-blur-xl shadow-lg' : 'bg-transparent'}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO} alt="Signature PSC" className="w-10 h-10 rounded-full object-contain bg-white p-0.5 shadow-md" />
          <div>
            <span className="text-sm font-bold tracking-wider text-white" style={{ fontFamily: "'Playfair Display', serif" }}>SIGNATURE</span>
            <span className="text-[10px] block text-[#c8b87a] tracking-[0.15em] uppercase -mt-0.5">PSC Coaching Centre</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-6">
          {['About', 'Results', 'Mock Test', 'Courses', 'Batches', 'Updates', 'Contact'].map(item => (
            <a key={item} href={`#${item.toLowerCase().replace(/\s/g, '')}`} className="text-sm text-white/80 hover:text-[#c8b87a] transition-colors">{item}</a>
          ))}
          <a href={`tel:+91${PHONE}`} className="hidden lg:flex items-center gap-1.5 text-sm text-[#c8b87a] hover:text-white transition-colors" data-testid="nav-call-btn">
            <Phone className="w-4 h-4" /> {PHONE}
          </a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-[#c8b87a] transition-colors" data-testid="nav-instagram" aria-label="Instagram">
            <Instagram className="w-5 h-5" />
          </a>
          <Link to="/login" data-testid="nav-login-btn">
            <Button className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white hover:from-[#d4b45a] hover:to-[#b08c3a] font-semibold px-6 rounded-full shadow-lg">
              Login
            </Button>
          </Link>
        </div>
        <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={open ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} /></svg>
        </button>
      </div>
      {open && (
        <div className="md:hidden bg-[#0c1a0e]/95 backdrop-blur-xl border-t border-white/10 px-6 py-4 space-y-3">
          {['About', 'Results', 'Mock Test', 'Courses', 'Batches', 'Updates', 'Contact'].map(item => (
            <a key={item} href={`#${item.toLowerCase().replace(/\s/g, '')}`} className="block text-sm text-white/80" onClick={() => setOpen(false)}>{item}</a>
          ))}
          <a href={`tel:+91${PHONE}`} className="flex items-center gap-2 text-sm text-[#c8b87a]"><Phone className="w-4 h-4" /> Call {PHONE}</a>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/80"><Instagram className="w-4 h-4" /> @signaturepsc</a>
          <Link to="/login" onClick={() => setOpen(false)}><Button className="w-full bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white rounded-full">Login</Button></Link>
        </div>
      )}
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="relative min-h-[100svh] lg:min-h-screen flex items-center pt-20" data-testid="hero-section">
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0c1a0e] via-[#142318] to-[#0a120c]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #c8a84e 0%, transparent 50%), radial-gradient(circle at 80% 20%, #c8a84e 0%, transparent 40%)' }} />
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(200,168,78,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(200,168,78,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
      </div>
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-12 py-12 lg:py-20 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <motion.div initial="hidden" animate="visible" className="lg:col-span-7">
          <motion.div variants={fadeUp} custom={0} className="flex flex-wrap items-center gap-2 mb-4 sm:mb-6">
            <div className="inline-flex items-center gap-2 bg-[#c8a84e]/15 border border-[#c8a84e]/30 rounded-full px-4 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-[11px] text-[#c8b87a] tracking-wider uppercase font-semibold">Trusted since 2015 · 10+ years</span>
            </div>
          </motion.div>
          <motion.h1 variants={fadeUp} custom={1} className="text-[2rem] sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05] font-bold mb-4 sm:mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            <span className="text-white">Kerala PSC dreams,</span><br />
            <span className="bg-gradient-to-r from-[#e8d48b] via-[#c8a84e] to-[#a07c2a] bg-clip-text text-transparent">delivered in Varkala.</span>
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} className="text-sm sm:text-base md:text-lg text-white/60 leading-relaxed mb-6 sm:mb-8 max-w-2xl">
            Specialised <strong className="text-white/90">LDC, Degree-level, LGS, Uniform Services</strong> and <strong className="text-white/90">Devaswom Board</strong> coaching. Daily current affairs, weekly mock tests, expert mentorship. Built by aspirants, for aspirants.
          </motion.p>
          {/* CTA buttons */}
          <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-2.5 sm:gap-3 mb-4 sm:mb-10">
            <a href={`https://wa.me/${WA_NUMBER}?text=Hi, I'm interested in PSC coaching at Signature.`} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn">
              <Button className="group bg-[#25D366] text-white hover:bg-[#128C7E] font-semibold px-5 sm:px-7 py-3 text-sm sm:text-base rounded-full shadow-[0_8px_24px_rgba(37,211,102,0.35)] hover:shadow-[0_12px_32px_rgba(37,211,102,0.5)] transition-all">
                <svg className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp Now
              </Button>
            </a>
            <a href={`tel:+91${PHONE}`} data-testid="hero-call-btn">
              <Button className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white hover:from-[#d4b45a] hover:to-[#b08c3a] font-semibold px-5 sm:px-7 py-3 text-sm sm:text-base rounded-full shadow-[0_4px_20px_rgba(200,168,78,0.3)]">
                <Phone className="w-4 h-4 mr-2" /> Call {PHONE}
              </Button>
            </a>
            <Link to="/daily-mock-test">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 font-semibold px-5 sm:px-7 py-3 text-sm sm:text-base rounded-full bg-transparent" data-testid="hero-mock-test-btn">
                Free Mock Test <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </motion.div>
          {/* Compact Google rating strip — mobile only, sits right beside CTAs */}
          <motion.a variants={fadeUp} custom={3.5} href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer"
            className="lg:hidden inline-flex items-center gap-2.5 mb-6 px-3.5 py-2 rounded-full bg-white/95 shadow-lg border border-white/30" data-testid="hero-google-trust-mobile">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            <span className="text-sm font-bold text-[#1a2e1c]">4.9</span>
            <span className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-[#fbbf24] text-[#fbbf24]" />)}</span>
            <span className="text-[11px] text-[#5a6b5c]">{GOOGLE_REVIEWS_COUNT} Google reviews</span>
            <ChevronRight className="w-3.5 h-3.5 text-[#4285F4]" />
          </motion.a>
          {/* Trust stats */}
          <motion.div variants={fadeUp} custom={4} className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-4 max-w-2xl">
            {[{ icon: Users, num: '500+', label: 'Students' }, { icon: Trophy, num: '50+', label: 'PSC Selections' }, { icon: BookOpen, num: '10+', label: 'Years' }, { icon: Target, num: 'Daily', label: 'Mock Tests' }].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5 sm:gap-3 p-2.5 sm:p-3 rounded-lg bg-white/5 border border-white/10 backdrop-blur-sm">
                <item.icon className="w-5 h-5 text-[#c8a84e] flex-shrink-0" />
                <div>
                  <p className="text-sm sm:text-base font-bold text-white leading-none">{item.num}</p>
                  <p className="text-[9px] sm:text-[10px] text-white/50 uppercase tracking-wider mt-0.5">{item.label}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right side: Trust panel — Google review card + WhatsApp card */}
        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="lg:col-span-5 space-y-3 lg:space-y-4">
          {/* Google Reviews trust card (desktop/tablet only — mobile uses the compact strip near CTAs) */}
          <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="group hidden lg:block p-6 rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.4)] hover:shadow-[0_24px_72px_rgba(200,168,78,0.3)] transition-all border border-white/20" data-testid="hero-google-trust">
            <div className="flex items-start gap-3 mb-4">
              <svg className="w-8 h-8 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <div className="flex-1">
                <p className="text-xs text-[#5a6b5c] uppercase tracking-wider font-semibold">Rated by Google</p>
                <p className="text-sm text-[#1a2e1c] mt-0.5">Signature PSC Coaching Centre</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <p className="text-5xl font-bold text-[#1a2e1c]" style={{ fontFamily: "'Playfair Display', serif" }}>4.9</p>
              <div>
                <div className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-[#fbbf24] text-[#fbbf24]" />)}</div>
                <p className="text-xs text-[#5a6b5c] mt-1">{GOOGLE_REVIEWS_COUNT} Google reviews</p>
              </div>
            </div>
            <p className="text-xs text-[#5a6b5c] italic leading-relaxed border-l-2 border-[#c8a84e]/40 pl-3 mb-3">"Best PSC coaching in Varkala. Teachers are very supportive and the daily mock tests really helped me crack LDC."</p>
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#5a6b5c]">Read all reviews on Google</span>
              <span className="text-[#4285F4] group-hover:translate-x-1 transition-transform font-semibold">→</span>
            </div>
          </a>
          {/* WhatsApp instant chat card */}
          <a href={`https://wa.me/${WA_NUMBER}?text=Hi, I'm interested in joining Signature PSC.`} target="_blank" rel="noopener noreferrer" className="group block p-4 sm:p-5 rounded-2xl bg-gradient-to-br from-[#25D366] to-[#128C7E] shadow-[0_20px_60px_rgba(37,211,102,0.3)] hover:shadow-[0_24px_72px_rgba(37,211,102,0.45)] transition-all">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </div>
              <div className="flex-1">
                <p className="text-xs text-white/80 uppercase tracking-wider font-semibold">Quick admission</p>
                <p className="text-base font-bold text-white mt-0.5">Chat with us on WhatsApp</p>
                <p className="text-xs text-white/80 mt-0.5">Usually replies within 30 mins</p>
              </div>
              <span className="text-white text-2xl group-hover:translate-x-1 transition-transform">→</span>
            </div>
          </a>
        </motion.div>
      </div>
    </section>
  );
}

function AboutSection() {
  return (
    <section id="about" className="py-24 md:py-32 bg-[#faf8f3]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <motion.div variants={fadeUp}>
            <span className="text-xs text-[#a07c2a] tracking-[0.2em] uppercase font-semibold">About Us</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 mb-6 text-[#1a2e1c]" style={{ fontFamily: "'Playfair Display', serif" }}>
              Best PSC Coaching Centre in Varkala — A Professional Centre for Aspiring Government Servants
            </h2>
            <p className="text-[#5a6b5c] leading-relaxed mb-6">
              SIGNATURE PSC COACHING CENTRE — <strong className="text-[#c8a84e]">a unit of Narzis Ventures LLP</strong> — operates branches at Palachira (Varkala) and Vizhavoor (Thiruvananthapuram) in Kerala. We are the best PSC coaching centre in the region, dedicated to shaping the future of aspirants preparing for Kerala PSC and competitive examinations. With 10+ years of proven excellence, we offer comprehensive coaching for LDC, LGS, Company Board Assistant, Uniform Services, Devaswom Board, and all government job examinations.
            </p>
            <div className="grid grid-cols-2 gap-4">
              {[{ icon: GraduationCap, text: "Dedicated Classrooms" }, { icon: Users, text: "Free Mentorship" }, { icon: FileText, text: "Printed Notes" }, { icon: Target, text: "Daily Exams" }].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-white border border-[#e8e0cc] shadow-sm">
                  <item.icon className="w-5 h-5 text-[#a07c2a] flex-shrink-0" />
                  <span className="text-sm text-[#1a2e1c] font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div variants={fadeUp} custom={1} className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl border border-[#e8e0cc]">
              <img src="https://images.unsplash.com/photo-1722573783625-eceb04251036?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBhZHVsdCUyMHN0dWRlbnRzJTIwc3R1ZHlpbmclMjBjb21wZXRpdGl2ZSUyMGV4YW0lMjBjb2FjaGluZyUyMGNsYXNzcm9vbXxlbnwwfHx8fDE3NzcwOTgzMjJ8MA&ixlib=rb-4.1.0&q=85" alt="PSC coaching classroom in Varkala - students preparing for Kerala PSC exams at Signature PSC Coaching Centre" className="w-full h-[350px] object-cover" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white p-5 rounded-xl shadow-xl">
              <span className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>10+</span>
              <span className="text-xs block opacity-90">Years of Excellence</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

function CoursesSection() {
  const courses = [
    { title: "LDC / LGS", desc: "Lower Division Clerk & Last Grade Servants preparation", icon: FileText },
    { title: "Company Board Assistant", desc: "Company/Corporation/Board Assistant examinations", icon: BookOpen },
    { title: "Uniform Services", desc: "Police, Excise, Forest, Fire Force coaching", icon: Shield },
    { title: "Devaswom Board", desc: "Travancore & Cochin Devaswom Board exams", icon: Star },
    { title: "Secretariat Assistant", desc: "Kerala Secretariat Assistant & Auditor posts", icon: Target },
    { title: "All Competitive Exams", desc: "General coaching for all Kerala PSC examinations", icon: Trophy },
  ];
  return (
    <section id="courses" className="py-24 md:py-32 bg-[#0c1a0e]" data-testid="courses-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
          <motion.span variants={fadeUp} className="text-xs text-[#c8a84e] tracking-[0.2em] uppercase font-semibold">Our Programs</motion.span>
          <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>PSC Coaching Courses in Varkala</motion.h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.1}
              className="group p-6 md:p-8 rounded-xl bg-[#142318] border border-white/5 hover:border-[#c8a84e]/30 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(200,168,78,0.1)]" data-testid={`course-card-${i}`}>
              <div className="w-10 h-10 rounded-lg bg-[#c8a84e]/10 flex items-center justify-center mb-4 group-hover:bg-[#c8a84e]/20 transition-colors">
                <course.icon className="w-5 h-5 text-[#c8a84e]" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{course.title}</h3>
              <p className="text-sm text-white/50 leading-relaxed">{course.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BatchesSection() {
  const batches = [
    { name: "Regular Batch", timing: "10:30 AM - 1:30 PM", days: "Monday to Saturday", icon: "morning" },
    { name: "Evening Batch", timing: "6:00 PM - 8:00 PM", days: "Monday to Saturday", icon: "evening" },
    { name: "Holiday Batch", timing: "10:00 AM - 3:30 PM", days: "Sundays & Holidays", icon: "holiday" },
    { name: "Free Batch", timing: "Flexible Timing", days: "For Eligible Students", icon: "free" },
  ];
  return (
    <section id="batches" className="py-24 md:py-32 bg-[#faf8f3]" data-testid="batches-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
          <motion.span variants={fadeUp} className="text-xs text-[#a07c2a] tracking-[0.2em] uppercase font-semibold">Schedule</motion.span>
          <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 text-[#1a2e1c]" style={{ fontFamily: "'Playfair Display', serif" }}>PSC Coaching Batches & Timings</motion.h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {batches.map((batch, i) => (
            <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.15}
              className="relative p-8 rounded-xl bg-white border border-[#e8e0cc] shadow-sm hover:shadow-xl transition-all duration-300 text-center" data-testid={`batch-card-${i}`}>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] rounded-t-xl" />
              <Clock className="w-8 h-8 text-[#a07c2a] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1a2e1c] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{batch.name}</h3>
              <p className="text-lg text-[#a07c2a] font-bold mb-1">{batch.timing}</p>
              <p className="text-sm text-[#5a6b5c]">{batch.days}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UpdatesSection() {
  const [tab, setTab] = useState('current_affairs');
  const [posts, setPosts] = useState({ current_affairs: [], notification: [], mock_question: [], video: [] });

  useEffect(() => {
    ['current_affairs', 'notification', 'mock_question', 'video'].forEach(t => {
      axios.get(`${API}/cms/posts?type=${t}&limit=12`).then(r => {
        setPosts(p => ({ ...p, [t]: r.data }));
      }).catch(() => {});
    });
  }, []);

  const tabs = [
    { id: 'current_affairs', label: 'Daily Current Affairs', icon: Newspaper, color: '#10b981' },
    { id: 'notification', label: 'PSC Notifications', icon: Bell, color: '#f59e0b' },
    { id: 'mock_question', label: 'Mock Questions', icon: HelpCircle, color: '#c8a84e' },
    { id: 'video', label: 'Short Videos', icon: Video, color: '#ef4444' },
  ];

  const getYoutubeEmbed = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?/\s]{11})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  };

  return (
    <section id="updates" className="py-24 md:py-32 bg-[#0c1a0e]" data-testid="updates-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
          <motion.span variants={fadeUp} className="text-xs text-[#c8a84e] tracking-[0.2em] uppercase font-semibold">Stay Updated</motion.span>
          <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            PSC Updates, Mock Tests & Daily Current Affairs Kerala
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-white/50 mt-3 max-w-2xl mx-auto text-sm">
            Daily current affairs, PSC notifications, mock questions and short tutorial videos — everything a Kerala PSC aspirant needs in one place.
          </motion.p>
        </motion.div>
        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="updates-tabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ${tab === t.id ? 'bg-[#c8a84e] text-[#0c1a0e] border-[#c8a84e]' : 'bg-[#142318] text-white/70 border-white/10 hover:border-[#c8a84e]/40'}`}
              data-testid={`updates-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
        {/* Content */}
        <div className="min-h-[200px]">
          {posts[tab].length === 0 ? (
            <div className="text-center py-12">
              {(() => { const T = tabs.find(t => t.id === tab); return <T.icon className="w-12 h-12 text-white/20 mx-auto mb-4" />; })()}
              <p className="text-white/40 text-sm">New {tabs.find(t => t.id === tab).label.toLowerCase()} coming soon...</p>
              <a href={`https://wa.me/${WA_NUMBER}?text=I want to get daily updates from Signature PSC.`} target="_blank" rel="noopener noreferrer" className="inline-block mt-4 text-[#c8a84e] hover:text-[#e8d48b] text-sm">Get updates on WhatsApp →</a>
            </div>
          ) : tab === 'video' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts[tab].map((p, i) => {
                const embed = getYoutubeEmbed(p.link);
                return (
                  <div key={p.id || i} className="rounded-xl bg-[#142318] border border-white/10 overflow-hidden" data-testid={`video-card-${i}`}>
                    {embed ? (
                      <div className="aspect-video"><iframe src={embed} title={p.title} className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe></div>
                    ) : (
                      <div className="aspect-video bg-black/50 flex items-center justify-center"><Video className="w-12 h-12 text-white/30" /></div>
                    )}
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-white">{p.title}</h3>
                      {p.content && <p className="text-xs text-white/50 mt-1">{p.content}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : tab === 'mock_question' ? (
            <div>
              <div className="mb-6 p-5 rounded-xl bg-gradient-to-br from-[#c8a84e]/10 to-[#0c1a0e] border border-[#c8a84e]/30 flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div>
                  <p className="text-sm text-[#c8b87a] uppercase tracking-wider font-semibold">Free Daily Mock Test</p>
                  <p className="text-lg text-white font-semibold mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>Take Today's 50-Question PSC Mock Test</p>
                  <p className="text-xs text-white/55 mt-1">+1 correct, -1/3 wrong · Instant result · No registration required</p>
                </div>
                <Link to="/daily-mock-test" data-testid="take-daily-test-btn">
                  <Button className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white font-semibold px-6 py-2.5 rounded-full whitespace-nowrap">Take Test Now →</Button>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {posts[tab].map((p, i) => <MockQuestionCard key={p.id || i} post={p} idx={i} />)}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts[tab].map((p, i) => (
                <motion.div key={p.id || i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.05}
                  className="p-5 rounded-xl bg-[#142318] border border-white/10 hover:border-[#c8a84e]/30 transition-all" data-testid={`update-card-${tab}-${i}`}>
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-[#c8a84e]/15 flex items-center justify-center flex-shrink-0">
                      {tab === 'current_affairs' ? <Newspaper className="w-4 h-4 text-[#c8a84e]" /> : <Bell className="w-4 h-4 text-[#c8a84e]" />}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-white leading-snug">{p.title}</h3>
                      <span className="text-[10px] text-white/40 uppercase tracking-wider">{p.created_at?.split('T')[0]}</span>
                    </div>
                  </div>
                  {p.content && <p className="text-xs text-white/60 leading-relaxed line-clamp-4 mb-3 whitespace-pre-wrap">{p.content}</p>}
                  {p.link && (
                    <a href={p.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#c8a84e] hover:text-[#e8d48b]">
                      View details <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function MockQuestionCard({ post, idx }) {
  const [selected, setSelected] = useState(null);
  const correct = post.correct_index;
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={idx * 0.05}
      className="p-5 rounded-xl bg-[#142318] border border-white/10" data-testid={`mock-q-card-${idx}`}>
      <div className="flex items-start gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-[#c8a84e] text-[#0c1a0e] flex items-center justify-center text-xs font-bold flex-shrink-0">Q</div>
        <h3 className="text-sm font-semibold text-white">{post.title}</h3>
      </div>
      <div className="space-y-2 mb-3">
        {(post.options || []).map((opt, i) => {
          const isSelected = selected === i;
          const isCorrect = selected !== null && i === correct;
          const isWrong = selected !== null && isSelected && i !== correct;
          return (
            <button key={i} onClick={() => selected === null && setSelected(i)} disabled={selected !== null}
              className={`w-full text-left text-xs px-3 py-2 rounded-lg border transition-all ${
                isCorrect ? 'border-emerald-500 bg-emerald-500/10 text-emerald-300' :
                isWrong ? 'border-red-500 bg-red-500/10 text-red-300' :
                isSelected ? 'border-[#c8a84e] bg-[#c8a84e]/10 text-white' :
                'border-white/10 text-white/70 hover:border-[#c8a84e]/40'
              }`}>
              <span className="font-bold mr-2">{String.fromCharCode(65 + i)}.</span>{opt}
              {isCorrect && <CheckCircle2 className="w-3.5 h-3.5 inline ml-2 text-emerald-400" />}
            </button>
          );
        })}
      </div>
      {selected !== null && post.explanation && (
        <div className="text-xs text-white/60 bg-black/30 p-3 rounded-lg border-l-2 border-[#c8a84e]">
          <span className="font-semibold text-[#c8a84e]">Explanation: </span>{post.explanation}
        </div>
      )}
    </motion.div>
  );
}

function MockTestPromo() {
  return (
    <section id="mocktest" className="py-24 md:py-32 bg-gradient-to-br from-[#142318] via-[#0c1a0e] to-[#142318] relative overflow-hidden" data-testid="mocktest-promo">
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 50% 50%, #c8a84e 0%, transparent 70%)' }} />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
          <motion.span variants={fadeUp} className="inline-block text-xs text-[#c8a84e] tracking-[0.25em] uppercase font-semibold mb-3">Free · No Registration</motion.span>
          <motion.h2 variants={fadeUp} custom={1} className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Test yourself with our <span className="bg-gradient-to-r from-[#e8d48b] to-[#c8a84e] bg-clip-text text-transparent">Daily PSC Mock Test</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-white/60 max-w-2xl mx-auto leading-relaxed">
            Take today's full-length mock test for free. Instant scoring with negative marking, detailed explanations, and a rank you can compare with our students.
          </motion.p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="grid md:grid-cols-2 gap-6 items-center">
          {/* Left: features */}
          <div className="space-y-4">
            {[
              { num: '50', label: 'Questions daily', desc: 'Curated by faculty matching the actual PSC pattern' },
              { num: '+1', label: 'Correct marking', desc: 'Standard Kerala PSC scoring system' },
              { num: '-1/3', label: 'Negative marking', desc: 'Train to think before you guess — like the real exam' },
              { num: '%', label: 'Instant result', desc: 'Score, ranking, and per-question explanation right after submit' },
            ].map((f, i) => (
              <div key={i} className="flex gap-4 p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#c8a84e]/30 transition-all backdrop-blur-sm">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#c8a84e]/30 to-[#a07c2a]/10 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg font-bold text-[#c8a84e]" style={{ fontFamily: "'Playfair Display', serif" }}>{f.num}</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.label}</p>
                  <p className="text-xs text-white/55 mt-1 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
          {/* Right: CTA card */}
          <div className="relative p-8 md:p-10 rounded-2xl bg-gradient-to-br from-[#c8a84e] via-[#b8943e] to-[#a07c2a] shadow-[0_24px_80px_rgba(200,168,78,0.4)]">
            <div className="absolute top-4 right-4 px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm text-[10px] font-bold text-white uppercase tracking-wider">Live</div>
            <Target className="w-12 h-12 text-white mb-4" />
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Today's Mock Test is ready</h3>
            <p className="text-white/85 text-sm mb-6 leading-relaxed">Just enter your name and WhatsApp number. No signup, no payment. We'll show your score instantly.</p>
            <Link to="/daily-mock-test">
              <Button className="bg-[#0c1a0e] text-white hover:bg-black font-semibold px-7 py-3 text-base rounded-full shadow-lg group" data-testid="mocktest-promo-cta">
                Take Today's Test <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <div className="mt-6 pt-6 border-t border-white/20 flex items-center gap-3 text-xs text-white/80">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Test releases at 8:00 AM IST · Available for 24 hours</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function SuccessCorner() {
  const [stories, setStories] = useState([]);
  useEffect(() => {
    axios.get(`${API}/success-stories`).then(r => setStories(r.data)).catch(() => {});
  }, []);

  return (
    <section id="results" className="py-24 md:py-32 bg-[#0c1a0e]" data-testid="success-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
          <motion.span variants={fadeUp} className="text-xs text-[#c8a84e] tracking-[0.2em] uppercase font-semibold">Our Pride</motion.span>
          <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Our Students in Government Jobs
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="text-white/50 mt-3 max-w-lg mx-auto">Students from Signature PSC Coaching Centre Varkala who achieved their dream government jobs</motion.p>
        </motion.div>
        {stories.length === 0 ? (
          <div className="text-center py-12">
            <Trophy className="w-12 h-12 text-[#c8a84e]/30 mx-auto mb-4" />
            <p className="text-white/30 text-sm">Success stories coming soon...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.map((s, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.1}
                className="p-6 rounded-xl bg-[#142318] border border-white/5 hover:border-[#c8a84e]/30 transition-all text-center" data-testid={`success-card-${i}`}>
                {s.photo_url ? (
                  <PhotoImg path={s.photo_url} alt={s.student_name} className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-2 border-[#c8a84e]/50" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-[#c8a84e]/10 flex items-center justify-center mx-auto mb-4">
                    <Award className="w-8 h-8 text-[#c8a84e]" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{s.student_name}</h3>
                <p className="text-[#c8a84e] font-semibold text-sm mt-1">{s.post}</p>
                <p className="text-white/50 text-xs mt-1">{s.exam} — {s.year}</p>
                {s.details && <p className="text-white/40 text-xs mt-2">{s.details}</p>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function TeachersSection() {
  const [teachers, setTeachers] = useState([]);
  useEffect(() => {
    axios.get(`${API}/teachers`).then(r => setTeachers(r.data)).catch(() => {});
  }, []);

  return (
    <section id="faculty" className="py-24 md:py-32 bg-[#faf8f3]" data-testid="teachers-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-16">
          <motion.span variants={fadeUp} className="text-xs text-[#a07c2a] tracking-[0.2em] uppercase font-semibold">Our Faculty</motion.span>
          <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 text-[#1a2e1c]" style={{ fontFamily: "'Playfair Display', serif" }}>
            Our Expert Faculty
          </motion.h2>
        </motion.div>
        {teachers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-[#a07c2a]/30 mx-auto mb-4" />
            <p className="text-[#5a6b5c]/50 text-sm">Faculty profiles coming soon...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teachers.map((t, i) => (
              <motion.div key={i} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={i * 0.1}
                className="p-6 rounded-xl bg-white border border-[#e8e0cc] shadow-sm hover:shadow-xl transition-all text-center" data-testid={`teacher-card-${i}`}>
                {t.photo_url ? (
                  <PhotoImg path={t.photo_url} alt={t.name} className="w-24 h-24 rounded-full object-cover mx-auto mb-4 border-3 border-[#c8a84e]/30 shadow-md" />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#c8a84e]/10 to-[#a07c2a]/10 flex items-center justify-center mx-auto mb-4">
                    <Briefcase className="w-10 h-10 text-[#a07c2a]" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-[#1a2e1c]" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</h3>
                <p className="text-[#a07c2a] font-medium text-sm mt-1">{t.specialization}</p>
                <p className="text-[#5a6b5c] text-xs mt-1">{t.experience} Experience</p>
                {t.details && <p className="text-[#5a6b5c]/70 text-xs mt-2">{t.details}</p>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setSending(true);
    try { await axios.post(`${API}/contact`, form); toast.success('Message sent!'); setForm({ name: '', email: '', phone: '', message: '' }); }
    catch { toast.error('Failed to send.'); }
    setSending(false);
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#faf8f3]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.span variants={fadeUp} className="text-xs text-[#a07c2a] tracking-[0.2em] uppercase font-semibold">Get In Touch</motion.span>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl font-semibold mt-3 mb-4 text-[#1a2e1c]" style={{ fontFamily: "'Playfair Display', serif" }}>Contact Signature PSC · Two Branches Across Kerala</motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-[#5a6b5c] mb-6">Now in <strong>Palachira (Varkala)</strong> and <strong>Vizhavoor (Thiruvananthapuram)</strong>. New branches opening shortly at Pongumoodu and Kollam Town.</motion.p>
            <motion.div variants={fadeUp} custom={3} className="space-y-4">
              <div className="p-4 rounded-xl bg-white border border-[#e8e0cc]" data-testid="branch-palachira">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#c8a84e]/15 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-[#a07c2a]" /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-[#1a2e1c]">Palachira Branch</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 uppercase font-semibold">Head Office</span>
                    </div>
                    <p className="text-xs text-[#5a6b5c] mt-0.5">Palachira, Varkala, Kollam Dist. · Kerala</p>
                    <p className="text-xs text-[#a07c2a] font-medium mt-1">📞 8281583404</p>
                  </div>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-white border border-[#e8e0cc]" data-testid="branch-vizhavoor">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#c8a84e]/15 flex items-center justify-center flex-shrink-0"><MapPin className="w-4 h-4 text-[#a07c2a]" /></div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-[#1a2e1c]">Vizhavoor Branch</p>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#c8a84e]/20 text-[#a07c2a] uppercase font-semibold">New</span>
                    </div>
                    <p className="text-xs text-[#5a6b5c] mt-0.5">Vizhavoor, Thiruvananthapuram · Kerala</p>
                    <p className="text-xs text-[#a07c2a] font-medium mt-1">📞 8281583404</p>
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-r from-[#faf1d6] to-[#f7e9c5] border border-[#e0d5aa]" data-testid="upcoming-branches">
                <p className="text-[10px] uppercase tracking-wider text-[#a07c2a] font-bold mb-1">Opening Shortly</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-white/70 text-[#1a2e1c] font-medium border border-[#e0d5aa]">Pongumoodu</span>
                  <span className="text-xs px-2.5 py-1 rounded-full bg-white/70 text-[#1a2e1c] font-medium border border-[#e0d5aa]">Kollam Town</span>
                  <span className="text-[10px] text-[#5a6b5c]">— more districts coming soon</span>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-1">
                <div className="w-9 h-9 rounded-lg bg-[#c8a84e]/10 flex items-center justify-center"><Phone className="w-4 h-4 text-[#a07c2a]" /></div>
                <div><span className="text-xs text-[#5a6b5c] uppercase tracking-wider">Phone / WhatsApp</span><p className="text-sm text-[#1a2e1c] font-medium">8281583404</p></div>
              </div>
            </motion.div>
            {/* WhatsApp CTA */}
            <motion.div variants={fadeUp} custom={4} className="mt-8 flex flex-wrap gap-3">
              <a href={`tel:+91${PHONE}`} className="inline-flex items-center gap-2 bg-[#c8a84e] text-[#0c1a0e] px-5 py-3 rounded-full font-semibold shadow-lg hover:bg-[#d4b45a] transition-all" data-testid="contact-call-btn">
                <Phone className="w-4 h-4" /> Call {PHONE}
              </a>
              <a href={`https://wa.me/${WA_NUMBER}?text=Hi, I'm interested in joining Signature PSC Coaching.`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:bg-[#128C7E] transition-all" data-testid="contact-whatsapp">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gradient-to-tr from-[#fdcb52] via-[#ee2a7b] to-[#6228d7] text-white px-5 py-3 rounded-full font-semibold shadow-lg hover:opacity-90 transition-all" data-testid="contact-instagram">
                <Instagram className="w-4 h-4" /> @signaturepsc
              </a>
            </motion.div>
            {/* Google Reviews */}
            <motion.div variants={fadeUp} custom={5} className="mt-6">
              <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-white border border-[#e8e0cc] shadow-sm hover:shadow-md transition-all" data-testid="contact-reviews-link">
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#fbbf24] text-[#fbbf24]" />)}
                    <span className="text-sm font-bold text-[#1a2e1c] ml-1">4.9</span>
                  </div>
                  <span className="text-xs text-[#5a6b5c]">{GOOGLE_REVIEWS_COUNT} Google Reviews</span>
                </div>
              </a>
            </motion.div>
            {/* Google Map */}
            <motion.div variants={fadeUp} custom={6} className="mt-6 rounded-xl overflow-hidden border border-[#e8e0cc] shadow-sm">
              <iframe
                title="Signature PSC Coaching Centre, Palachira, Varkala — Location"
                src="https://www.google.com/maps?q=Signature+PSC+Coaching+Centre+Palachira+Varkala&hl=en&z=15&output=embed"
                width="100%" height="280" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map"></iframe>
            </motion.div>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
            <form onSubmit={handleSubmit} className="p-6 md:p-8 rounded-xl bg-white border border-[#e8e0cc] shadow-sm space-y-4" data-testid="contact-form">
              <Input placeholder="Your Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                className="bg-[#faf8f3] border-[#e8e0cc] text-[#1a2e1c] placeholder:text-[#9ca38f] focus:border-[#c8a84e] focus:ring-1 focus:ring-[#c8a84e]" data-testid="contact-name-input" />
              <Input type="email" placeholder="Email Address" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                className="bg-[#faf8f3] border-[#e8e0cc] text-[#1a2e1c] placeholder:text-[#9ca38f] focus:border-[#c8a84e]" data-testid="contact-email-input" />
              <Input placeholder="Phone Number" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} required
                className="bg-[#faf8f3] border-[#e8e0cc] text-[#1a2e1c] placeholder:text-[#9ca38f] focus:border-[#c8a84e]" data-testid="contact-phone-input" />
              <Textarea placeholder="Your Message" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required rows={4}
                className="bg-[#faf8f3] border-[#e8e0cc] text-[#1a2e1c] placeholder:text-[#9ca38f] focus:border-[#c8a84e] resize-none" data-testid="contact-message-input" />
              <Button type="submit" disabled={sending}
                className="w-full bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white hover:from-[#d4b45a] hover:to-[#b08c3a] font-semibold rounded-full shadow-lg" data-testid="contact-submit-btn">
                {sending ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-8 bg-[#0c1a0e] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Logo" className="w-6 h-6 rounded-full bg-white p-0.5" />
          <span className="text-sm text-white/50">SIGNATURE PSC COACHING CENTRE</span>
        </div>
        <p className="text-xs text-white/40 mt-1">A unit of <strong className="text-white/60">Narzis Ventures LLP</strong></p>
        <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} All rights reserved. Palachira · Vizhavoor · Kerala</p>
      </div>
    </footer>
  );
}

// Floating WhatsApp Button
function WhatsAppFloat() {
  return (
    <a href={`https://wa.me/${WA_NUMBER}?text=Hi, I want to know more about Signature PSC Coaching.`} target="_blank" rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-[0_4px_20px_rgba(37,211,102,0.4)] hover:scale-110 transition-transform" data-testid="whatsapp-float">
      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
    </a>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen" data-testid="landing-page">
      <Navbar />
      <HeroSection />
      <SuccessCorner />
      <MockTestPromo />
      <CoursesSection />
      <BatchesSection />
      <UpdatesSection />
      <AboutSection />
      <TeachersSection />
      <ContactSection />
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
